import type { PropsWithChildren, ForwardedRef } from 'react';
import { forwardRef } from 'react';

import type { ButtonProps } from '@mui/material';
import { Button } from '@mui/material';
import { styled } from '@mui/material/styles';

import CircularProgress from './circular-progress';
import Skeleton from './skeleton';

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'size',
})(({ theme, size }) => {
  let padding = '';
  let vPadding = '6px';
  if (size === 'small') vPadding = '4px';
  else if (size === 'large') vPadding = '8px';
  padding = `${vPadding} 16px`;

  return {
    padding,
    '&.Mui-disabled': {
      color: theme.palette.text.disabled,
    },
  };
});

type TRef = HTMLButtonElement | null;

interface PreloadProps {
  height?: string | number;
  width?: string | number;
  className?: string;
}

interface IProps extends ButtonProps {
  preload?: boolean;
  loading?: boolean;
  width?: number | string;
  height?: number | string;
}

function Preloader({ height, width, className }: PreloadProps) {
  return <Skeleton height={height} width={width} className={className ?? ''} variant="rectangular" />;
}

function TempButton(
  { preload, width, height = 38, loading, children, ...rest }: PropsWithChildren<IProps>,
  ref: ForwardedRef<TRef>
) {
  const { variant = 'outlined', className = '', disabled, ...remaining } = rest as ButtonProps;

  if (preload) {
    return <Preloader width={width} height={height} className={className} />;
  }

  return (
    <StyledButton
      ref={ref}
      className={`${className} rounded-lg normal-case`}
      disabled={loading || disabled}
      variant={variant}
      {...remaining}
    >
      <>
        {children}
        {loading && <CircularProgress size={24} className="absolute left-1/2 top-1/2 -ml-3 -mt-3" />}
      </>
    </StyledButton>
  );
}

const AtpButton = forwardRef<TRef, PropsWithChildren<IProps>>(TempButton);

export default AtpButton;
